import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Shield, 
  FileText, 
  MessageSquare, 
  Users,
  Truck,
  HardHat,
  ArrowRight,
  Check,
  FolderKanban,
  Lock,
  FileCheck,
  Search,
  KeyRound
} from 'lucide-react';

const features = [
  {
    icon: FolderKanban,
    title: "Department-Based Access",
    description: "Control who sees what. Assign team members to specific departments and they only see documents in their departments."
  },
  {
    icon: FileText,
    title: "Multi-Format Documents",
    description: "Upload PDFs, Word documents, Excel spreadsheets, and text files. All your company knowledge in one place."
  },
  {
    icon: MessageSquare,
    title: "Intelligent Q&A",
    description: "Ask questions about operations, safety procedures, or logistics and get accurate answers with citations."
  },
  {
    icon: Users,
    title: "Team Management",
    description: "Invite team members and control their access to different departments based on their role."
  },
  {
    icon: Lock,
    title: "Secure & Private",
    description: "Your documents stay secure. Role-based access ensures sensitive data is only visible to authorized users."
  },
  {
    icon: Search,
    title: "AI-Powered Search",
    description: "Intelligent search across all your documents. Find the exact information you need in seconds."
  }
];

const categories = [
  "Logistics",
  "Safety", 
  "Financial",
  "Operations",
  "HR",
  "Admin",
  "Project Management",
  "IT",
  "Business Development",
  "Engineering"
];

export default function LandingPage() {

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group" data-testid="landing-logo">
              <img 
                src="/rogers-logo.png" 
                alt="Rogers Trucking" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-xl font-bold text-foreground">Rogers OPS</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-nav-btn">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 gradient-highlight" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-6 space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium">
                <Lock className="w-4 h-4" />
                Secure Document Intelligence
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Your Company Knowledge,{' '}
                <span className="text-primary">Secured & Accessible</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl">
                Private SaaS for your company documents. Get instant answers with role-based access control - sensitive data stays protected.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <Button size="lg" className="btn-glow w-full sm:w-auto" data-testid="hero-cta-btn">
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Role-based access
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Secure & private
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary" />
                  Instant answers
                </div>
              </div>
            </div>

            {/* Visual - Document Vault Animation */}
            <div className="lg:col-span-6 relative h-[400px] lg:h-[500px] flex items-center justify-center">
              <div className="absolute inset-0">
                {/* Background glow */}
                <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-[#C9B79C]/10 rounded-full blur-[80px]" />
                
                {/* Document Vault Visual */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    {/* Main Vault */}
                    <div className="w-56 h-56 rounded-2xl bg-gradient-to-br from-[#333333] to-[#555E63] border-2 border-[#555E63]/50 flex flex-col items-center justify-center shadow-2xl relative overflow-hidden">
                      {/* Lock icon */}
                      <div className="w-16 h-16 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center mb-3 z-10">
                        <KeyRound className="w-8 h-8 text-primary" />
                      </div>
                      <span className="text-[#C9B79C] font-semibold text-sm z-10">SECURED</span>
                      
                      {/* Scanlines effect */}
                      <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(211, 84, 0, 0.1) 2px, rgba(211, 84, 0, 0.1) 4px)'
                      }} />
                    </div>
                    
                    {/* Floating Documents */}
                    <div className="absolute -top-6 -left-12 animate-bounce" style={{ animationDuration: '3s' }}>
                      <div className="w-14 h-18 rounded-lg bg-card border border-border p-2 shadow-lg">
                        <div className="w-full h-1 bg-red-400/60 rounded mb-1" />
                        <div className="w-3/4 h-1 bg-muted rounded mb-1" />
                        <div className="w-1/2 h-1 bg-muted rounded" />
                        <FileText className="w-4 h-4 text-red-400 mt-2" />
                      </div>
                    </div>
                    
                    <div className="absolute -top-4 -right-14 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>
                      <div className="w-14 h-18 rounded-lg bg-card border border-border p-2 shadow-lg">
                        <div className="w-full h-1 bg-blue-400/60 rounded mb-1" />
                        <div className="w-2/3 h-1 bg-muted rounded mb-1" />
                        <div className="w-4/5 h-1 bg-muted rounded" />
                        <FileCheck className="w-4 h-4 text-blue-400 mt-2" />
                      </div>
                    </div>
                    
                    <div className="absolute -bottom-8 -left-8 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                      <div className="w-14 h-18 rounded-lg bg-card border border-border p-2 shadow-lg">
                        <div className="w-full h-1 bg-emerald-400/60 rounded mb-1" />
                        <div className="w-1/2 h-1 bg-muted rounded mb-1" />
                        <div className="w-3/4 h-1 bg-muted rounded" />
                        <Shield className="w-4 h-4 text-emerald-400 mt-2" />
                      </div>
                    </div>
                    
                    <div className="absolute -bottom-6 -right-10 animate-bounce" style={{ animationDuration: '3.2s', animationDelay: '1.5s' }}>
                      <div className="w-14 h-18 rounded-lg bg-card border border-border p-2 shadow-lg">
                        <div className="w-full h-1 bg-purple-400/60 rounded mb-1" />
                        <div className="w-4/5 h-1 bg-muted rounded mb-1" />
                        <div className="w-1/2 h-1 bg-muted rounded" />
                        <Lock className="w-4 h-4 text-purple-400 mt-2" />
                      </div>
                    </div>
                    
                    {/* Connection lines */}
                    <svg className="absolute inset-0 w-full h-full" style={{ transform: 'scale(2.5)' }}>
                      <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="rgba(211, 84, 0, 0)" />
                          <stop offset="50%" stopColor="rgba(211, 84, 0, 0.5)" />
                          <stop offset="100%" stopColor="rgba(211, 84, 0, 0)" />
                        </linearGradient>
                      </defs>
                      <circle cx="50%" cy="50%" r="45%" fill="none" stroke="url(#lineGradient)" strokeWidth="1" strokeDasharray="4 4" className="animate-spin" style={{ animationDuration: '30s' }} />
                      <circle cx="50%" cy="50%" r="35%" fill="none" stroke="rgba(85, 94, 99, 0.3)" strokeWidth="1" strokeDasharray="2 6" className="animate-spin" style={{ animationDuration: '20s', animationDirection: 'reverse' }} />
                    </svg>
                  </div>
                </div>
                
                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-5" style={{ 
                  backgroundImage: 'linear-gradient(hsl(24 100% 41% / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(24 100% 41% / 0.3) 1px, transparent 1px)',
                  backgroundSize: '40px 40px'
                }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Department Access Section */}
      <section className="py-20 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Organize by Department
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Control access to sensitive documents. Team members only see departments they're assigned to.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {categories.map((category, index) => (
              <div 
                key={category}
                className="px-4 py-2 rounded-full bg-card border border-border text-foreground text-sm font-medium hover:border-primary/50 transition-colors"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {category}
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">Admin/Manager Access</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Admins and managers see all documents across every department. Full visibility for management who need it.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#C9B79C]/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-[#C9B79C]" />
                </div>
                <h3 className="font-semibold text-foreground">Department-Based Access</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Team members only see their assigned departments. Sensitive documents hidden from those who don't need them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-card/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything Your Team Needs
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Replace the need for constant questions. Your documents become an always-available knowledge assistant.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  className="p-6 rounded-xl bg-card border border-border card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Replace Knowledge Gaps
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              When key team members leave, their knowledge doesn't have to go with them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Logistics Coordinator</h3>
              <p className="text-muted-foreground text-sm mb-4">
                "What's our shipping schedule with supplier X?"
              </p>
              <p className="text-muted-foreground text-sm">
                "What's the procedure for expedited orders?"
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center mb-4">
                <HardHat className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Shop Foreman</h3>
              <p className="text-muted-foreground text-sm mb-4">
                "What's the safety procedure for operating the press?"
              </p>
              <p className="text-muted-foreground text-sm">
                "What's the maintenance schedule for machine #3?"
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card border border-border">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Operations Manager</h3>
              <p className="text-muted-foreground text-sm mb-4">
                "What's our policy on overtime?"
              </p>
              <p className="text-muted-foreground text-sm">
                "What were last quarter's safety incidents?"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/rogers-logo.png" 
                alt="Rogers Trucking" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-semibold text-foreground">Rogers OPS</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Rogers OPS. Secure Document Intelligence.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
